import React, { useEffect, useState } from 'react';
import {
  Package,
  Shield,
  Plus,
  BookOpen,
  Layers,
  Eye,
  DollarSign
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import StatsCard from '@/components/admin/StatsCard';
import CourseCard from '@/components/admin/CourseCard';
import BundleCard from '@/components/admin/BundleCard';
import CourseFormModal from '@/components/admin/CourseFormModal';
import BundleFormModal from '@/components/admin/BundleFormModal';
import VisibilityManagerModal from '@/components/admin/VisibilityManagerModal';
import adminProductsService from '@/services/adminProducts.service';
import { authService } from '@/services';
import type { CourseCategory, SkillLevel } from '@/types/api.types';

interface Course {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  category: CourseCategory;
  is_active: boolean;
  hidden: boolean;
  duration_hours?: number;
  skill_level?: SkillLevel;
  thumbnail_url?: string;
  created_at: string;
  updated_at: string;
}

interface Bundle {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  course_ids: string[];
  hidden: boolean;
  is_active: boolean;
  thumbnail_url?: string;
  savings_percentage?: number;
  created_at: string;
  updated_at: string;
}

interface Stats {
  totalProducts: number;
  activeCourses: number;
  totalBundles: number;
}

interface CourseFormData {
  title: string;
  description: string;
  full_price: string;
  discount_price: string;
  hidden: boolean;
  category: CourseCategory;
  is_active: boolean;
  duration_hours: string;
  skill_level: SkillLevel;
  thumbnail_url: string;
}

interface BundleFormData {
  title: string;
  description: string;
  full_price: string;
  discount_price: string;
  hidden: boolean;
  course_ids: string[];
  is_active: boolean;
  thumbnail_url: string;
  savings_percentage: string;
}

const initialCourseForm: CourseFormData = {
  title: '',
  description: '',
  full_price: '',
  discount_price: '',
  category: 'Core Skills',
  is_active: true,
  hidden: false,
  duration_hours: '',
  skill_level: 'Beginner',
  thumbnail_url: ''
};

const initialBundleForm: BundleFormData = {
  title: '',
  description: '',
  full_price: '',
  discount_price: '',
  course_ids: [],
  is_active: true,
  hidden: false,
  thumbnail_url: '',
  savings_percentage: ''
};

export default function AdminProducts() {
  const [user, setUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'courses' | 'bundles'>('courses');
  const [courses, setCourses] = useState<Course[]>([]);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [stats, setStats] = useState<Stats>({ totalProducts: 0, activeCourses: 0, totalBundles: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Course modal state
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [courseForm, setCourseForm] = useState<CourseFormData>(initialCourseForm);

  // Bundle modal state
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);
  const [bundleForm, setBundleForm] = useState<BundleFormData>(initialBundleForm);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [coursesData, bundlesData] = await Promise.all([fetchCourses(), fetchBundles()]);
      updateStats(coursesData, bundlesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await adminProductsService.fetchCourses();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch courses');
      }
      const data = response.data || [];
      setCourses(data);
      return data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  };

  const fetchBundles = async () => {
    try {
      const response = await adminProductsService.fetchBundles();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch bundles');
      }
      const data = response.data || [];
      setBundles(data);
      return data;
    } catch (error) {
      console.error('Error fetching bundles:', error);
      throw error;
    }
  };

  const updateStats = (coursesData: Course[], bundlesData: Bundle[]) => {
    const activeCourses = coursesData.filter(c => c.is_active).length;
    const totalBundles = bundlesData.length;
    const totalProducts = coursesData.length + bundlesData.length;
    setStats({ totalProducts, activeCourses, totalBundles });
  };

  // ============================================================================
  // Course Handlers
  // ============================================================================

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await adminProductsService.createCourse(courseForm);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create course');
      }
      await fetchCourses();
      closeCourseModal();
    } catch (error) {
      console.error('Error creating course:', error);
      setError('Failed to create course. Please try again.');
    }
  };

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCourse) return;

    try {
      const response = await adminProductsService.updateCourse(editingCourse.id, courseForm);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update course');
      }
      await fetchCourses();
      closeCourseModal();
    } catch (error) {
      console.error('Error updating course:', error);
      setError('Failed to update course. Please try again.');
    }
  };

  const handleDeleteCourse = async (id: string) => {
    if (!confirm('Are you sure you want to delete this course?')) return;

    try {
      const response = await adminProductsService.deleteCourse(id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete course');
      }
      await fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      setError('Failed to delete course. Please try again.');
    }
  };

  const startEditCourse = (course: Course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      description: course.description,
      full_price: course.full_price.toString(),
      discount_price: course.discount_price?.toString() || '',
      category: course.category,
      is_active: course.is_active,
      hidden: course.hidden,
      duration_hours: course.duration_hours?.toString() || '',
      skill_level: course.skill_level || 'Beginner',
      thumbnail_url: course.thumbnail_url || ''
    });
    setShowCourseModal(true);
  };

  const openNewCourseModal = () => {
    setEditingCourse(null);
    setCourseForm(initialCourseForm);
    setShowCourseModal(true);
  };

  const closeCourseModal = () => {
    setShowCourseModal(false);
    setEditingCourse(null);
    setCourseForm(initialCourseForm);
  };

  // ============================================================================
  // Bundle Handlers
  // ============================================================================

  const handleCreateBundle = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await adminProductsService.createBundle(bundleForm);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create bundle');
      }
      await fetchBundles();
      closeBundleModal();
    } catch (error) {
      console.error('Error creating bundle:', error);
      setError('Failed to create bundle. Please try again.');
    }
  };

  const handleUpdateBundle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBundle) return;

    try {
      const response = await adminProductsService.updateBundle(editingBundle.id, bundleForm);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update bundle');
      }
      await fetchBundles();
      closeBundleModal();
    } catch (error) {
      console.error('Error updating bundle:', error);
      setError('Failed to update bundle. Please try again.');
    }
  };

  const handleDeleteBundle = async (id: string) => {
    if (!confirm('Are you sure you want to delete this bundle?')) return;

    try {
      const response = await adminProductsService.deleteBundle(id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete bundle');
      }
      await fetchBundles();
    } catch (error) {
      console.error('Error deleting bundle:', error);
      setError('Failed to delete bundle. Please try again.');
    }
  };

  const startEditBundle = (bundle: Bundle) => {
    setEditingBundle(bundle);
    setBundleForm({
      title: bundle.title,
      description: bundle.description,
      full_price: bundle.full_price.toString(),
      discount_price: bundle.discount_price?.toString() || '',
      course_ids: bundle.course_ids,
      is_active: bundle.is_active,
      hidden: bundle.hidden,
      thumbnail_url: bundle.thumbnail_url || '',
      savings_percentage: bundle.savings_percentage?.toString() || ''
    });
    setShowBundleModal(true);
  };

  const openNewBundleModal = () => {
    setEditingBundle(null);
    setBundleForm(initialBundleForm);
    setShowBundleModal(true);
  };

  const closeBundleModal = () => {
    setShowBundleModal(false);
    setEditingBundle(null);
    setBundleForm(initialBundleForm);
  };

  const toggleCourseInBundle = (courseId: string) => {
    setBundleForm(prev => ({
      ...prev,
      course_ids: prev.course_ids.includes(courseId)
        ? prev.course_ids.filter(id => id !== courseId)
        : [...prev.course_ids, courseId]
    }));
  };

  return (
    <AdminLayout>
      <section className="relative py-12 px-6 overflow-hidden min-h-screen">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
              <Shield className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400">Admin Access</span>
            </div>
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Product Management
              </span>
            </h1>
            <p className="text-xl text-gray-400">
              Welcome back, <span className="text-white font-semibold">{user?.username || 'Admin'}</span>
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
              {error}
              <button 
                onClick={() => setError(null)}
                className="ml-4 text-red-300 hover:text-red-100"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <StatsCard
              icon={Package}
              value={stats.totalProducts}
              label="Total Products"
              iconColor="text-purple-400"
            />
            <StatsCard
              icon={BookOpen}
              value={stats.activeCourses}
              label="Active Courses"
              iconColor="text-blue-400"
            />
            <StatsCard
              icon={Layers}
              value={stats.totalBundles}
              label="Bundles"
              iconColor="text-cyan-400"
            />
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mb-8 border-b border-white/10">
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'courses'
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Courses ({courses.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('bundles')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'bundles'
                  ? 'text-purple-400 border-b-2 border-purple-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Layers className="w-5 h-5" />
                <span>Bundles ({bundles.length})</span>
              </div>
            </button>
          </div>

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div>
              {/* Add Course Button */}
              <div className="mb-6 flex justify-between items-center">
                <button
                  onClick={openNewCourseModal}
                  className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add New Course</span>
                </button>
                <button
                  onClick={() => setShowVisibilityModal(true)}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
                >
                  <Eye className="w-5 h-5" />
                  <span>Visibility Manager</span>
                </button>
              </div>

              {/* Courses List */}
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-12 text-gray-400">Loading courses...</div>
                ) : courses.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">No courses yet. Create your first course!</div>
                ) : (
                  courses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      onEdit={startEditCourse}
                      onDelete={handleDeleteCourse}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Bundles Tab */}
          {activeTab === 'bundles' && (
            <div>
              {/* Add Bundle Button */}
              <div className="mb-6 flex justify-between items-center">
                <button
                  onClick={openNewBundleModal}
                  className="flex items-center space-x-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add New Bundle</span>
                </button>
              </div>

              {/* Bundles List */}
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-12 text-gray-400">Loading bundles...</div>
                ) : bundles.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">No bundles yet. Create your first bundle!</div>
                ) : (
                  bundles.map((bundle) => (
                    <BundleCard
                      key={bundle.id}
                      bundle={bundle}
                      onEdit={startEditBundle}
                      onDelete={handleDeleteBundle}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Modals */}
      <CourseFormModal
        isOpen={showCourseModal}
        onClose={closeCourseModal}
        onSubmit={editingCourse ? handleUpdateCourse : handleCreateCourse}
        formData={courseForm}
        setFormData={setCourseForm}
        editingCourse={editingCourse}
      />

      <BundleFormModal
        isOpen={showBundleModal}
        onClose={closeBundleModal}
        onSubmit={editingBundle ? handleUpdateBundle : handleCreateBundle}
        formData={bundleForm}
        setFormData={setBundleForm}
        editingBundle={editingBundle}
        courses={courses}
        onToggleCourse={toggleCourseInBundle}
      />

      <VisibilityManagerModal
        isOpen={showVisibilityModal}
        onClose={() => setShowVisibilityModal(false)}
        courses={courses}
        bundles={bundles}
        onRefresh={fetchData}
      />
    </AdminLayout>
  );
}
